import React, { useState } from "react";
import {
  card,
  cardInner,
  cardTitle,
  cardTitleWrap,
  comingSoonTitle,
  cardsWrap,
  cardsWrapGrid,
} from "../styles/DashboardStyles";
import { fetchApps } from 'scripts/fetchApps'
import { fetchReports } from "scripts/fetchReports";
import { useNavigate } from "react-router-dom";
import CardsCarousel from "components/CardsCarousel";
import App from "interfaces/App.interface";
import ReportsTile from "components/ReportsTile";
import { ReportData } from "interfaces/ReportData.interface";

const DashboardTiles: React.FC<{ user?: string }> = ({ user }) => {
  const [apps, setApps] = useState<App[] | undefined>([]);
  const [background, setBackground] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [guestConnectionCount, setGuestConnectionCount] = useState(0);
  const [selectedReportDate, setSelectedReportDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const navigate = useNavigate();

  const isGuestConnectionApp = (app: App) => {
    const name = (app.name || "").toLowerCase();
    return name.includes("guest connection");
  };

  React.useEffect(() => {
    const fetchData = async () => {
      const appsResponse = await fetchApps();
      setApps(appsResponse?.filter((item: App) => !item?.name?.toLowerCase().includes('background')));
      setBackground(appsResponse?.filter((item: App) => item?.name?.toLowerCase().includes('background'))[0]?.image);
    };
    fetchData();
  }, []);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const styleId = "guest-connection-badge-pulse";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes badgePulse {
        0% { box-shadow: 0 0 0 0 rgba(225, 6, 0, 0.55); }
        70% { box-shadow: 0 0 0 8px rgba(225, 6, 0, 0); }
        100% { box-shadow: 0 0 0 0 rgba(225, 6, 0, 0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const getLocalDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const isWithinPollingHours = () => {
      const now = new Date();
      const hour = now.getHours();
      return hour >= 8 && hour < 20;
    };

    const isTodaySelected = () => {
      return selectedReportDate === getLocalDateString(new Date());
    };

    const fetchReportData = async () => {
      setReportsLoading(true);
      const reportResponse = await fetchReports({
        startDate: selectedReportDate,
        endDate: selectedReportDate,
      });
      if (!isMounted) return;
      setReportData(reportResponse);
      setReportsLoading(false);
    };

    fetchReportData();

    intervalId = setInterval(() => {
      if (isWithinPollingHours() && isTodaySelected()) {
        fetchReportData();
      }
    }, 3600000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedReportDate]);

  React.useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const getStoreNumber = () => {
      try {
        return localStorage.getItem("storeNumber");
      } catch {
        return null;
      }
    };

    const fetchGuestConnectionTickets = async () => {
      const storeId = getStoreNumber();
      if (!storeId) {
        if (isMounted) setGuestConnectionCount(0);
        return;
      }

      const url =
        `https://api.edible.tech/zendesk/v1/gettickets?` +
        `storeId=${encodeURIComponent(storeId)}` +
        `&brand=31944278133140` +
        `&subscription-key=78824a5c6e674e6fa7f425abd31ab679`;

      try {
        const response = await fetch(url, { method: "GET" });
        if (!response.ok) {
          console.error("Guest Connection tickets error:", response.status, response.statusText);
          return;
        }

        const data = await response.json();
        const count = typeof data?.count === "number" ? data.count : 0;

        if (!isMounted) return;
        setGuestConnectionCount(count);
      } catch (error) {
        console.error("Error fetching Guest Connection tickets:", error);
      }
    };

    fetchGuestConnectionTickets();
    intervalId = setInterval(fetchGuestConnectionTickets, 300000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!apps) return null;

  const appsWithBadges = apps.map((app) =>
    isGuestConnectionApp(app) ? { ...app, badgeCount: guestConnectionCount } : app
  );
  const activeApps = appsWithBadges.filter(app => app.isActive !== false);
  const comingSoonApps = appsWithBadges.filter(app => app.isActive === false);

  return (
    <>
      <CardsCarousel
        apps={activeApps}
        user={user || ""}
        card={card}
        cardInner={cardInner}
        cardTitleWrap={cardTitleWrap}
        cardTitle={cardTitle}
        navigate={navigate}
        containerStyle={comingSoonApps && comingSoonApps.length > 0 ?
          ({...cardsWrap, background: `url(${background}) center/cover no-repeat`}) :
          ({...cardsWrapGrid, background: `url(${background}) center/cover no-repeat`})}
      />
      <ReportsTile
        report={reportData}
        loading={reportsLoading}
        selectedDate={selectedReportDate}
        onDateChange={setSelectedReportDate}
        autoCollapsed={true}
      />
      {comingSoonApps && comingSoonApps.length > 0 && (
        <>
          <div style={comingSoonTitle}>Coming Soon</div>
          <CardsCarousel
            apps={comingSoonApps}
            user={user || ""}
            card={card}
            cardInner={cardInner}
            cardTitleWrap={cardTitleWrap}
            cardTitle={cardTitle}
            navigate={navigate}
            containerStyle={{ ...cardsWrap, background: 'none', position: 'relative' }}
          />
        </>
      )}
    </>
  );
};

export default DashboardTiles;
